import { useState } from 'react';
import { TextInput, Select, Textarea } from '@mantine/core';
import { IconTrash, IconPencil, IconCheck, IconX } from '@tabler/icons-react';
import { FlexibleDateInput } from './FlexibleDateInput';
import type { PriorEventLocal } from '../../types/medicalHistory';

interface PriorEventFormProps {
  event: PriorEventLocal;
  onChange: (updated: PriorEventLocal) => void;
  onRemove: () => void;
  onSubmit: () => void;
  disabled?: boolean;
  isNew?: boolean;
}

const STATUS_OPTIONS = [
  { value: 'resolved', label: 'Resolved' },
  { value: 'remission', label: 'In Remission' },
];

// Format date for display
function formatDate(date: PriorEventLocal['eventDate']): string {
  if (!date) return '';
  const { value, precision } = date;
  if (precision === 'year') return value;
  if (precision === 'month') {
    const [year, month] = value.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month, 10) - 1]} ${year}`;
  }
  const d = new Date(value);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function PriorEventForm({
  event,
  onChange,
  onRemove,
  onSubmit,
  disabled = false,
  isNew = false,
}: PriorEventFormProps) {
  const [isEditing, setIsEditing] = useState(isNew);

  const handleChange = (
    field: keyof PriorEventLocal,
    value: string | PriorEventLocal['eventDate'] | PriorEventLocal['status']
  ) => {
    onChange({ ...event, [field]: value });
  };

  const handleSave = () => {
    if (event.eventName.trim()) {
      onSubmit();
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    if (isNew) {
      onRemove();
    } else {
      setIsEditing(false);
    }
  };

  // View mode - display as card
  if (!isEditing) {
    return (
      <div className="card p-4 group">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-gray-200">{event.eventName}</h4>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                event.status === 'resolved'
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {event.status === 'resolved' ? 'Resolved' : 'In Remission'}
              </span>
            </div>
            <div className="mt-1 text-sm text-gray-400 space-y-0.5">
              {event.eventDate && (
                <p>{formatDate(event.eventDate)}</p>
              )}
              {event.comments && (
                <p className="text-gray-500 italic">{event.comments}</p>
              )}
            </div>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={disabled}
              className="p-1.5 rounded-lg text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors disabled:opacity-50"
              title="Edit"
            >
              <IconPencil size={16} />
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
              title="Delete"
            >
              <IconTrash size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Edit mode - show form
  return (
    <div className="card p-4 border-cyan-500/30">
      <div className="space-y-4">
        {/* Event Name */}
        <TextInput
          label="Illness or Event"
          placeholder="e.g., ACL Tear, Pneumonia, Cancer Diagnosis"
          value={event.eventName}
          onChange={(e) => handleChange('eventName', e.target.value)}
          disabled={disabled}
          required
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
          }}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Date/Year */}
          <div className="md:col-span-2">
            <FlexibleDateInput
              label="When did this occur?"
              value={event.eventDate}
              onChange={(date) => handleChange('eventDate', date)}
              disabled={disabled}
            />
          </div>

          {/* Status */}
          <Select
            label="Current Status"
            placeholder="Select status"
            data={STATUS_OPTIONS}
            value={event.status}
            onChange={(val) =>
              handleChange('status', (val as 'resolved' | 'remission') || 'resolved')
            }
            disabled={disabled}
            classNames={{
              input: 'input',
              label: 'text-sm font-medium text-gray-300 mb-1',
              dropdown: 'bg-gray-900 border-gray-700',
              option: 'hover:bg-gray-800 text-gray-200',
            }}
          />
        </div>

        {/* Comments */}
        <Textarea
          label="Additional Details"
          placeholder="Share anything helpful — e.g., how it was treated, how long it lasted, any lasting effects"
          value={event.comments || ''}
          onChange={(e) => handleChange('comments', e.target.value)}
          disabled={disabled}
          minRows={2}
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
          }}
        />

        {/* Action buttons */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={disabled}
            className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            <IconX size={14} />
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={disabled || !event.eventName.trim()}
            className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <IconCheck size={14} />
            {isNew ? 'Add Event' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
